import React, { useEffect } from "react"

import {
  ANNOTATION_VALUE,
  type AnnotationValue,
} from "@/constants/annotation-values"
import { useTableFocus } from "@/human-feedback/focus"

import type { TargetCondition } from "@/types/metrics/contracts"

import {
  getAnnotationMetricColor,
  yesNoBadgeColorClasses,
  yesNoBadgeNAClasses,
  yesNoBadgeShapeClasses,
} from "../utils/annotationBadge"

interface AnnotationSelectorProps {
  selectedValue?: AnnotationValue | null
  onValueChange: (value: AnnotationValue | null) => void
  metricId: string // Add metricId to identify which selector is active
  targetCondition?: TargetCondition
  readOnly?: boolean
}

export const AnnotationSelector: React.FC<AnnotationSelectorProps> = ({
  selectedValue,
  onValueChange,
  metricId,
  targetCondition,
  readOnly = false,
}) => {
  const getDisplayText = (value: AnnotationValue): string => {
    switch (value) {
      case ANNOTATION_VALUE.YES:
        return "Yes"
      case ANNOTATION_VALUE.NO:
        return "No"
      case ANNOTATION_VALUE.UNKNOWN:
        return "N/A"
    }
  }

  const handleValueClick = () => {
    if (readOnly) {
      return
    }
    // Clear the selection to show buttons again
    onValueChange(null)
  }

  const { registerValueChangeCallback, unregisterValueChangeCallback } =
    useTableFocus()

  // Register this metric's onValueChange callback with the parent table handler
  useEffect(() => {
    if (readOnly) {
      return
    }
    registerValueChangeCallback(metricId, onValueChange)
    return () => unregisterValueChangeCallback(metricId)
  }, [
    metricId,
    onValueChange,
    readOnly,
    registerValueChangeCallback,
    unregisterValueChangeCallback,
  ])

  const getSelectedButtonStyles = (value: AnnotationValue): string => {
    if (value === ANNOTATION_VALUE.UNKNOWN) {
      return `${yesNoBadgeShapeClasses} ${yesNoBadgeNAClasses} cursor-pointer w-12`
    }
    const color = getAnnotationMetricColor(value, targetCondition)
    return `${yesNoBadgeShapeClasses} ${yesNoBadgeColorClasses[color]} cursor-pointer w-12`
  }

  // Unselected state: default background (match runs page), square chip shape
  const unselectedButtonClasses = `${yesNoBadgeShapeClasses} w-12 cursor-pointer bg-surface-1 text-coval-text hover:bg-surface-1-hover`

  return (
    <div className="flex items-center justify-center">
      {selectedValue ? (
        <button
          type="button"
          className={getSelectedButtonStyles(selectedValue)}
          onClick={handleValueClick}
          disabled={readOnly}
        >
          {getDisplayText(selectedValue)}
        </button>
      ) : (
        <div className="flex text-xs space-x-2 items-center justify-center">
          {(
            [
              ANNOTATION_VALUE.YES,
              ANNOTATION_VALUE.NO,
              ANNOTATION_VALUE.UNKNOWN,
            ] as const
          ).map((value) => (
            <button
              key={value}
              type="button"
              className={unselectedButtonClasses}
              onClick={() => {
                if (!readOnly) {
                  onValueChange(value)
                }
              }}
              disabled={readOnly}
            >
              {getDisplayText(value)}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
